import { Alert, AlertDescription } from "@groot/ui/alert";
import { Button } from "@groot/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@groot/ui/card";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Textarea } from "@groot/ui/textarea";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import { AlertCircle, FileText, Upload, X } from "lucide-react";
import { useState } from "react";

interface AccountStatementUploadProps {
  accountId: number;
  onUploadComplete: () => void;
}

export function AccountStatementUpload({
  accountId,
  onUploadComplete,
}: AccountStatementUploadProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    uploaded: number;
    failed: Array<{ fileName: string; error: string }>;
  } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      // Validate files
      const invalidFiles = files.filter((file) => file.type !== "application/pdf");
      if (invalidFiles.length > 0) {
        toast({
          variant: "destructive",
          title: "Invalid Files",
          description: "Only PDF files are allowed",
        });
        return;
      }

      // Validate file names (should be YYYY-MM.pdf)
      const invalidNames = files.filter((file) => {
        const nameWithoutExt = file.name.replace(/\.pdf$/i, "");
        return !/^\d{4}-(0[1-9]|1[0-2])$/.test(nameWithoutExt);
      });

      if (invalidNames.length > 0) {
        toast({
          variant: "destructive",
          title: "Invalid File Names",
          description: "File names must be in YYYY-MM.pdf format (e.g., 2024-01.pdf)",
        });
        return;
      }

      if (files.length > 50) {
        toast({
          variant: "destructive",
          title: "Too Many Files",
          description: "Maximum 50 files can be uploaded at once",
        });
        return;
      }

      setSelectedFiles(files);
      setUploadResult(null);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      toast({
        variant: "destructive",
        title: "No Files Selected",
        description: "Please select at least one PDF file",
      });
      return;
    }

    try {
      setUploading(true);
      const result = await apiClient.bulkUploadStatements({
        accountId,
        pdfs: selectedFiles,
        notes: notes.trim() || undefined,
      });

      setUploadResult({
        uploaded: result.uploaded.length,
        failed: result.failed,
      });

      if (result.uploaded.length > 0) {
        toast({
          title: "Upload Successful",
          description: `Successfully uploaded ${result.uploaded.length} statement(s)`,
        });

        // Reset form
        setSelectedFiles([]);
        setNotes("");

        // Notify parent component
        onUploadComplete();
      }

      if (result.failed.length > 0) {
        toast({
          variant: "destructive",
          title: "Some Uploads Failed",
          description: `${result.failed.length} file(s) failed to upload. See details below.`,
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload statements",
      });
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload Statements</CardTitle>
        <CardDescription>
          Upload one or more statement PDFs. File names must be in YYYY-MM.pdf format (e.g.,
          2024-01.pdf)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="pdfs">PDF Files</Label>
            <Input
              id="pdfs"
              type="file"
              accept=".pdf,application/pdf"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
            />
            <p className="text-sm text-muted-foreground">
              Maximum 50 files. Each file must be named YYYY-MM.pdf
            </p>
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Files ({selectedFiles.length})</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                {selectedFiles.map((file, index) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: file object doesn't have a unique id
                    key={index}
                    className="flex items-center justify-between p-2 bg-muted rounded-md"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="size-4 text-muted-foreground" />
                      <span className="text-sm">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about these statements..."
              rows={3}
              disabled={uploading}
            />
          </div>

          {/* Upload Result */}
          {uploadResult && uploadResult.failed.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">Failed Uploads:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {uploadResult.failed.map((failed, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: failure list is static
                      <li key={index} className="text-sm">
                        <strong>{failed.fileName}</strong>: {failed.error}
                      </li>
                    ))}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={uploading || selectedFiles.length === 0}
            className="w-full"
          >
            {uploading ? (
              <>Uploading...</>
            ) : (
              <>
                <Upload className="size-4" />
                Upload{" "}
                {selectedFiles.length > 0 &&
                  `(${selectedFiles.length} file${selectedFiles.length > 1 ? "s" : ""})`}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
